<template lang="pug">
.quantity-picker(data-test-id="deal-quantity-picker")
  QuantityPickerPerSegment(
    v-if="usesSegments"
    :quantitiesPerClassification="dealQuantitiesPerClassification"
    @changeSegmentQuantity="changeSegmentQuantity"
    )
  BriqInput(
    v-else
    v-model="dealQuantity"
    type="number"
    step="1"
    min="1"
    )
</template>

<script>
import {
  eqProps,
  pipe,
  prop,
  forEach,
  find,
  sum,
  map,
  pathOr,
  any,
  values,
  toPairs,
  propEq,
} from 'ramda'
import { sumProp, propDo } from '@v/util/briqRamdaExt.js'
import QuantityPickerPerSegment from './QuantityPickerPerSegment.vue'
import {
  orderQuantitiesByProductGroupProductKey,
  mapQuantitiesToDictionary,
} from './DealQuantityPickerHelpers/SegmentedQuantityCalculations.js'
import {
  segmentlessQuantitiesByProductGroupProductKey,
  segmentlessDealQuantity,
} from './DealQuantityPickerHelpers/SegmentlessQuantityCalculations.js'
import {
  mutateAndGetQuantitiesPerClassificationGroupedByProductGroupProductKey,
  totalQuantitiesPerClassification,
  sumQuantities,
} from './DealQuantityPickerHelpers/dealQuantityPickerHelperFunctions.js'
import { bookingProductIsSplit } from '@v/bookings/edit/util/bookingDealFunctions.js'
import { mapGetters } from 'vuex'

export default {
  name: 'DealQuantityPicker',
  components: { QuantityPickerPerSegment },
  props: {
    bookingDeal: { type: Object, required: true },
  },
  data() {
    return {
      internalDealQuantity: null,
    }
  },
  computed: {
    ...mapGetters('bookingUpsert', ['flatSegments']),
    dealKey() {
      return this.bookingDeal.dealKey
    },
    dealQuantity: {
      get() {
        return this.internalDealQuantity
      },
      set(newQuantity) {
        this.bookingDeal.products.forEach(p => {
          if (!this.bookingProductIsSplit(p.ProductGroupProductKey)) {
            p.ProductQuantity = parseInt(newQuantity) * p.QuantityInProductGroup
          }
        })
        this.internalDealQuantity = parseInt(newQuantity)
      },
    },
    dealQuantitiesPerClassification() {
      return pipe(
        toPairs,
        map(([k, v]) => {
          const segment = find(propEq('Code', k), this.flatSegments)
          return {
            SegmentCode: k,
            SegmentName: segment?.NamePlural || 'unknown',
            Order: segment?.Ordering,
            Quantity: v,
          }
        }),
        x => [{ ClassificationCode: 'age', QuantitiesPerSegment: x }],
      )(this.bookingDeal.quantities.perSegment)
    },
    totalQuantity() {
      return pipe(
        pathOr([], [0, 'QuantitiesPerSegment']),
        sumProp('Quantity'),
      )(this.dealQuantitiesPerClassification)
    },
    totalQuantityPerDeal() {
      return {
        dealKey: this.bookingDeal.dealKey,
        total: sumQuantities(this.dealQuantitiesPerClassification) || this.dealQuantity || 0,
      }
    },
    usesSegments() {
      return !this.bookingDeal.isSegmentless
    },
    segmentlessDealQuantity() {
      return segmentlessDealQuantity(this.segmentlessTotalsByProductGroupProductKey)
    },
    segmentlessQuantitiesByProductGroupProductKey() {
      return segmentlessQuantitiesByProductGroupProductKey({
        bookingDeal: this.bookingDeal,
        logError: this.$logError,
      })
    },
    segmentlessTotalsByProductGroupProductKey() {
      return map(sum)(this.segmentlessQuantitiesByProductGroupProductKey)
    },
    quantitiesByProductGroupProductKey() {
      return orderQuantitiesByProductGroupProductKey({
        products: this.bookingDeal.products,
        normalizeQuantities: false,
      })
    },
    totalQuantitiesPerClassificationOfDeal() {
      return mapQuantitiesToDictionary(this.dealQuantitiesPerClassification)
    },
    quantitiesPerClassificationGroupedByProductGroupProductKey() {
      return mutateAndGetQuantitiesPerClassificationGroupedByProductGroupProductKey(this.$logError)(
        this.bookingDeal,
      )
    },
    totalQuantitiesPerClassification() {
      return totalQuantitiesPerClassification(
        this.bookingDeal,
        this.quantitiesPerClassificationGroupedByProductGroupProductKey,
        this.intersectedQuantitiesPerClassification,
        this.quantitiesByProductGroupProductKey,
      )
    },
  },
  methods: {
    changeSegmentQuantity(segment, newDealQuantity) {
      // mutate bookingproduct quantities
      pipe(
        prop('products'),
        forEach(bookingProduct => {
          if (bookingProduct.isSegmentless) {
            bookingProduct.ProductQuantity =
              this.totalQuantity * bookingProduct.QuantityInProductGroup
          } else if (!this.bookingProductIsSplit(bookingProduct.ProductGroupProductKey)) {
            const setMultipliedSegmentQuantity = setSegmentQuantity({
              segment,
              newQuantity: parseInt(newDealQuantity) * bookingProduct.QuantityInProductGroup,
            })
            forEach(setMultipliedSegmentQuantity)(bookingProduct.QuantitiesPerClassification)
          }
        }),
      )(this.bookingDeal)
      // mutate bookingdeal quantities
      this.bookingDeal.quantities.perSegment[segment.SegmentCode] = newDealQuantity
      this.bookingDeal.quantities.total = pipe(values, sum)(this.bookingDeal.quantities.perSegment)
    },
    anyBookingProductIsSplit() {
      return anyBookingProductIsSplit(this.bookingDeal)
    },
    bookingProductIsSplit(productGroupProductKey) {
      return bookingProductIsSplit(this.bookingDeal)(productGroupProductKey)
    },
  },
  beforeMount() {
    this.dealQuantity = this.segmentlessDealQuantity
  },
  updated() {
    this.dealQuantity = this.segmentlessDealQuantity
  },
}

// utility functions

const findSegment = s => find(eqProps('SegmentCode')(s))

const setSegmentQuantity = ({ segment, newQuantity }) =>
  pipe(prop('QuantitiesPerSegment'), findSegment(segment), x =>
    x ? (x.Quantity = parseInt(newQuantity)) : null,
  )

const anyBookingProductIsSplit = bookingDeal =>
  any(propDo('ProductGroupProductKey', bookingProductIsSplit(bookingDeal)), bookingDeal.products)
</script>

<style scoped lang="stylus">
.quantity-picker
  position relative
  height 38px
</style>
